import { BlockNoteEditor } from '@blocknote/core';
import {
  FormattingToolbar,
  FormattingToolbarController,
  BlockTypeSelect,
  BlockTypeSelectItem,
  BasicTextStyleButton,
  CreateLinkButton,
  UnnestBlockButton,
  NestBlockButton,
  useBlockNoteEditor
} from '@blocknote/react';
import { IconH1, IconH2, IconH3, IconHeading, IconList, IconListNumbers } from '@tabler/icons-react';
import { useCallback } from 'react';

export const blockTypeSelectItems = (): BlockTypeSelectItem[] => [
  {
    name: 'Paragraph',
    type: 'paragraph',
    icon: IconHeading,
    isSelected: block => block.type === 'paragraph'
  },
  {
    name: 'Heading 1',
    type: 'heading',
    props: { level: 1 },
    icon: IconH1,
    isSelected: block => block.type === 'heading' && block.props?.level === 1
  },
  {
    name: 'Heading 2',
    type: 'heading',
    props: { level: 2 },
    icon: IconH2,
    isSelected: block => block.type === 'heading' && block.props?.level === 2
  },
  {
    name: 'Heading 3',
    type: 'heading',
    props: { level: 3 },
    icon: IconH3,
    isSelected: block => block.type === 'heading' && block.props?.level === 3
  },
  {
    name: 'Bullet List',
    type: 'bulletListItem',
    icon: IconList,
    isSelected: block => block.type === 'bulletListItem'
  },
  {
    name: 'Numbered List',
    type: 'numberedListItem',
    icon: IconListNumbers,
    isSelected: block => block.type === 'numberedListItem'
  }
];

export default function TextEditorFormattingToolbar() {
  const editor = useBlockNoteEditor();

  const isNestableBlock = useCallback((editor: BlockNoteEditor, variant: 'nest' | 'unnest') => {
    const block = editor?.getTextCursorPosition()?.block;

    const isListItem = block?.type === 'bulletListItem' || block?.type === 'numberedListItem';

    if (!isListItem) {
      return false;
    }

    const prevBlock = editor?.getTextCursorPosition()?.prevBlock;
    const isTopLevelBlock = editor.topLevelBlocks.includes(block);
    const isPreviousBlockListItem = prevBlock?.type === 'bulletListItem' || prevBlock?.type === 'numberedListItem';

    if (isTopLevelBlock && !isPreviousBlockListItem) {
      return false;
    }

    return variant === 'nest' ? editor.canNestBlock() : editor.canUnnestBlock();
  }, []);

  return (
    <FormattingToolbarController
      formattingToolbar={() => (
        <FormattingToolbar>
          <BlockTypeSelect items={blockTypeSelectItems()} />
          <BasicTextStyleButton basicTextStyle="bold" />
          <BasicTextStyleButton basicTextStyle="italic" />
          {isNestableBlock(editor, 'unnest') && <UnnestBlockButton />}
          {isNestableBlock(editor, 'nest') && <NestBlockButton />}
          <CreateLinkButton />
        </FormattingToolbar>
      )}
    />
  );
}
